import React from 'react';
import { Wrench } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections/constants';
import { getProviderFromToolKey } from '@cotera/client/v0/components/tool-connections/utils';
import type { AgentTool } from '../../use-agent-node-config';

/**
 * Wrapped row of tool chips (provider icon + tool name) for an agent's toolset.
 * Shared between the node card (capped via `max` + a `+N` pill) and the node
 * editor pop-up (uncapped) so both render an agent's tools identically.
 */
export const AgentToolChips: React.FC<{
  tools: AgentTool[];
  /** Cap the number of chips shown, collapsing the rest into a `+N` pill. */
  max?: number;
}> = ({ tools, max }) => {
  const visible = max !== undefined ? tools.slice(0, max) : tools;
  const overflow = tools.length - visible.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((tool) => {
        const Icon = PROVIDER_ICONS[getProviderFromToolKey(tool.key)];
        return (
          <span
            key={tool.key}
            title={tool.name}
            className="inline-flex max-w-full items-center gap-1 rounded-md border bg-muted/40 px-1.5 py-0.5 [&_svg]:h-3 [&_svg]:w-3"
          >
            {Icon !== undefined ? (
              <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                <Icon />
              </span>
            ) : (
              <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            <Text.Caption className="truncate text-muted-foreground">
              {tool.name}
            </Text.Caption>
          </span>
        );
      })}
      {overflow > 0 ? (
        <span className="rounded-md border bg-muted/40 px-1.5 py-0.5">
          <Text.Caption className="text-muted-foreground">
            +{overflow}
          </Text.Caption>
        </span>
      ) : null}
    </div>
  );
};
